/**
 * Button Configurator - matches legacy /tmp/add-line-key-form.php
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { hardwareService } from '../../services/hardwareService';
import api from '../../services/api';
import type { LineKey, ExpansionKey, PhoneAccount } from './types';

interface ButtonConfiguratorProps {
  open: boolean;
  onClose: () => void;
  hardwareId: number;
  buttonNo: number | null;
  isExpansion: boolean;
  currentConfig?: LineKey | ExpansionKey;
  accounts: PhoneAccount[];
  totalLineKeys: number;
  configuredKeys: number[];
  customerId?: number;
}

const LINE_KEY_TYPES = {
  SPEED_DIAL: 13,
  LINE: 15,
  BLF: 16,
};

const ButtonConfigurator: React.FC<ButtonConfiguratorProps> = ({
  open,
  onClose,
  hardwareId,
  buttonNo,
  isExpansion,
  currentConfig,
  accounts,
  totalLineKeys,
  configuredKeys,
  customerId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extensions, setExtensions] = useState<Array<{ extension: string; name: string }>>([]);

  // Get custid from accounts (all accounts should have the same CustID)
  const custId = accounts.length > 0 ? accounts[0].CustID : null;

  // Form fields
  const [selectedButtonNo, setSelectedButtonNo] = useState<number>(0);
  const [provisionType, setProvisionType] = useState<number>(0);
  const [numberType, setNumberType] = useState<string>(''); // Extension or Phone
  const [extension, setExtension] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [account, setAccount] = useState<number | ''>('');
  const [blfExtension, setBlfExtension] = useState<string>('');

  // Fetch extensions
  useEffect(() => {
    if (open) {
      fetchExtensions();
    }
  }, [open, customerId]);

  const fetchExtensions = async () => {
    try {
      const params: any = { userOnly: false };
      if (customerId) {
        params.customerId = customerId;
      }
      const response = await api.get('/endpoints/extensions', {
        params
      });
      if (response.data?.success && response.data?.data?.extensions) {
        // Map to simple format: { extension, name }
        const exts = response.data.data.extensions.map((ext: any) => ({
          extension: ext.extension,
          name: ext.name || ext.fullname || ext.extension
        }));
        setExtensions(exts);
      }
    } catch (err) {
      console.error('Failed to load extensions:', err);
    }
  };

  // Load existing config
  useEffect(() => {
    if (currentConfig) {
      // Check if it's a regular line key
      if ('linekey.type' in currentConfig) {
        const keyNo = currentConfig.LineKeyNo;
        const type = currentConfig['linekey.type'];
        const value = currentConfig['linekey.value'] || '';
        const label = currentConfig['linekey.label'] || '';
        const line = currentConfig['linekey.line'];
        const pickupValue = currentConfig['linekey.pickup_value'] || '';

        setSelectedButtonNo(keyNo);
        setProvisionType(Number(type)); // Convert to number for state
        setDescription(label);

        // Type is stored as string in database
        const typeStr = String(type);

        if (typeStr === '13') {
          // Speed Dial - check if Extension (4 digits) or Phone (10 digits)
          if (value.length === 4) {
            setNumberType('Extension');
            setExtension(value);
          } else {
            setNumberType('Phone');
            setPhoneNumber(value);
          }
        } else if (typeStr === '15') {
          // Line - convert line number to number for state
          setAccount(line ? Number(line) : '');
        } else if (typeStr === '16') {
          // BLF
          setBlfExtension(pickupValue);
        }
      }
      // Check if it's an expansion key
      else if ('expansion_module.key.type' in currentConfig) {
        const keyNo = currentConfig.KeyNo;
        const type = currentConfig['expansion_module.key.type'];
        const value = currentConfig['expansion_module.key.value'] || '';
        const label = currentConfig['expansion_module.key.label'] || '';
        const line = currentConfig['expansion_module.key.line'];
        const pickupValue = currentConfig['expansion_module.key.pickup_value'] || '';

        setSelectedButtonNo(keyNo);
        setProvisionType(Number(type)); // Convert to number for state
        setDescription(label);

        // Type is stored as string in database
        const typeStr = String(type);

        if (typeStr === '13') {
          // Speed Dial - check if Extension (4 digits) or Phone (10 digits)
          if (value.length === 4) {
            setNumberType('Extension');
            setExtension(value);
          } else {
            setNumberType('Phone');
            setPhoneNumber(value);
          }
        } else if (typeStr === '15') {
          // Line - convert line number to number for state
          setAccount(line ? Number(line) : '');
        } else if (typeStr === '16') {
          // BLF
          setBlfExtension(pickupValue);
        }
      }
    } else {
      // New - pre-select button number if provided
      setSelectedButtonNo(buttonNo || 0);
      setProvisionType(0);
      setNumberType('');
      setExtension('');
      setPhoneNumber('');
      setDescription('');
      setAccount('');
      setBlfExtension('');
    }
  }, [currentConfig, buttonNo, open]);

  const availableButtons = Array.from(
    { length: isExpansion ? 40 : totalLineKeys },
    (_, i) => i + 1
  ).filter(keyNo => !configuredKeys.includes(keyNo) || keyNo === buttonNo);

  const handleSave = async () => {
    setError(null);

    // Validation matching legacy system
    if (!selectedButtonNo) {
      setError('Please select a Button #');
      return;
    }

    if (!provisionType) {
      setError('Please Select A Provision Type For This Button');
      return;
    }

    switch (provisionType) {
      case LINE_KEY_TYPES.SPEED_DIAL:
        if (!numberType) {
          setError('Please Select A Number Type');
          return;
        }
        if (numberType === 'Extension') {
          if (!extension) {
            setError('Please Select An Extension');
            return;
          }
        } else {
          if (!phoneNumber || !description) {
            setError('Please enter the Phone and Description');
            return;
          }
          if (phoneNumber.length !== 10) {
            setError('Phone Number must be 10 digits');
            return;
          }
        }
        break;
      case LINE_KEY_TYPES.LINE:
        if (!account) {
          setError('Please Select An Account');
          return;
        }
        break;
      case LINE_KEY_TYPES.BLF:
        if (!blfExtension) {
          setError('Please Select An Extension');
          return;
        }
        break;
    }

    try {
      setLoading(true);

      const saveData: any = {
        buttonNo: selectedButtonNo,
        type: provisionType,
        value: '',
        label: '',
        line: null,
        pickupValue: '',
        extension: null,
      };

      switch (provisionType) {
        case LINE_KEY_TYPES.SPEED_DIAL:
          if (numberType === 'Extension') {
            // Auto-populate label with extension name
            const selectedExt = extensions.find(e => e.extension === extension);
            saveData.value = extension;
            saveData.label = selectedExt?.name || extension;
          } else {
            saveData.value = phoneNumber;
            saveData.label = description;
          }
          break;
        case LINE_KEY_TYPES.LINE:
          // Find the selected account to get its extension
          console.log('LINE: account selected:', account, 'type:', typeof account);
          console.log('LINE: accounts:', accounts.map(a => ({ AcctLineNo: a.AcctLineNo, type: typeof a.AcctLineNo, Extension: a.Extension })));
          const selectedAcct = accounts.find(a => a.AcctLineNo == account); // Use loose equality
          console.log('LINE: found account:', selectedAcct);
          saveData.line = account;
          saveData.extension = selectedAcct?.Extension;
          console.log('LINE: saveData.extension =', saveData.extension);

          if (!saveData.extension) {
            setError('Could not find extension for selected account');
            setLoading(false);
            return;
          }
          break;
        case LINE_KEY_TYPES.BLF:
          // Get fullname from extensions list for BLF label
          const selectedExt = extensions.find(e => e.extension === blfExtension);
          saveData.pickupValue = blfExtension;
          saveData.extension = blfExtension;
          saveData.value = `${custId}_${blfExtension}`;
          saveData.label = selectedExt?.name || '';
          break;
      }

      await hardwareService.updateLineKey(hardwareId, selectedButtonNo, saveData, isExpansion, customerId);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {currentConfig ? `Edit Line Key ${selectedButtonNo}` : 'Add Line Key'}
        {isExpansion && ' (Expansion Module)'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Button # */}
        <TextField
          fullWidth
          required
          select
          label="Button #"
          value={selectedButtonNo}
          onChange={(e) => setSelectedButtonNo(parseInt(e.target.value))}
          disabled={!!currentConfig || !!buttonNo}
          sx={{ mb: 2, mt: 1 }}
        >
          <MenuItem value={0}>Select Button</MenuItem>
          {availableButtons.map((keyNo) => (
            <MenuItem key={keyNo} value={keyNo}>
              Button {keyNo}
            </MenuItem>
          ))}
        </TextField>

        {/* Provision Type */}
        <TextField
          fullWidth
          required
          select
          label="Provision Type"
          value={provisionType}
          onChange={(e) => {
            setProvisionType(parseInt(e.target.value));
            // Reset other fields
            setNumberType('');
            setExtension('');
            setPhoneNumber('');
            setDescription('');
            setAccount('');
            setBlfExtension('');
          }}
          sx={{ mb: 2 }}
        >
          <MenuItem value={0}>Select Type</MenuItem>
          <MenuItem value={LINE_KEY_TYPES.SPEED_DIAL}>Speed Dial</MenuItem>
          <MenuItem value={LINE_KEY_TYPES.LINE}>Line</MenuItem>
          <MenuItem value={LINE_KEY_TYPES.BLF}>BLF</MenuItem>
        </TextField>

        {/* Speed Dial Fields */}
        {provisionType === LINE_KEY_TYPES.SPEED_DIAL && (
          <>
            <TextField
              fullWidth
              required
              select
              label="Number Type"
              value={numberType}
              onChange={(e) => {
                setNumberType(e.target.value);
                setExtension('');
                setPhoneNumber('');
                setDescription('');
              }}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select Type</MenuItem>
              <MenuItem value="Extension">Extension</MenuItem>
              <MenuItem value="Phone">Phone</MenuItem>
            </TextField>

            {numberType === 'Extension' && (
              <TextField
                fullWidth
                required
                select
                label="Extension"
                value={extension}
                onChange={(e) => setExtension(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Select Extension</MenuItem>
                {extensions.map((ext) => (
                  <MenuItem key={ext.extension} value={ext.extension}>
                    {ext.extension} - {ext.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {numberType === 'Phone' && (
              <>
                <TextField
                  fullWidth
                  required
                  label="Phone #"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="10 digit phone number"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  required
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </>
        )}

        {/* Line Fields */}
        {provisionType === LINE_KEY_TYPES.LINE && (
          <TextField
            fullWidth
            required
            select
            label="Account"
            value={account}
            onChange={(e) => setAccount(parseInt(e.target.value))}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select Account</MenuItem>
            {accounts.filter(a => a['account.enable'] === 1).map((acct) => (
              <MenuItem key={acct.AccountID} value={acct.AcctLineNo}>
                {acct.AcctLineNo} - {acct.Extension}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* BLF Fields */}
        {provisionType === LINE_KEY_TYPES.BLF && (
          <TextField
            fullWidth
            required
            select
            label="Extension"
            value={blfExtension}
            onChange={(e) => setBlfExtension(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">Select Extension</MenuItem>
            {extensions.map((ext) => (
              <MenuItem key={ext.extension} value={ext.extension}>
                {ext.extension} - {ext.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ButtonConfigurator;
